import locale from "@bonliva-crm/locale";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  ConsultantAssignCallListFormValues,
  consultantAssignCallListActionFormSchema
} from "../components/actions/action-form-values";
import { AssignCallListAction } from "../components/actions/assign-call-list";
import { ExternalActionStepProps } from "../utils/action-step-props";
import { BulkActionHookReturn } from "../utils/bulk-action-data";
import { SelectionStepRowType } from "../utils/selection-step-row-type";
import { useBulkActions } from "./use-bulk-actions";

type Props<T extends SelectionStepRowType> = {
  valueParser: ExternalActionStepProps<T>["valueParsers"]["ConsultantAssignCallList"];
  selectedRows: T[];
  totalRows: number;
  entitySingular: string;
  entityPlural: string;
};

export const useConsultantAssignCallListAction = <T extends SelectionStepRowType>(
  props: Props<T>
): BulkActionHookReturn => {
  const classes = useStyles();
  const { valueParser, selectedRows, totalRows, entitySingular, entityPlural } = props;

  const parsedValues = useMemo(
    () => valueParser(selectedRows),
    [valueParser, selectedRows]
  );

  const consultantAssignCallListActionForm = useForm<ConsultantAssignCallListFormValues>({
    defaultValues: {
      callOrContactLists: []
    },
    mode: "onChange",
    resolver: yupResolver(consultantAssignCallListActionFormSchema)
  });

  const { assignConsultantCallList } = useBulkActions({});

  const actionContent = (
    <AssignCallListAction form={consultantAssignCallListActionForm} />
  );

  const confirmationContent = (
    <Box className={classes.confirmationContent}>
      <Typography variant="body1">
        {locale.callAndContactListConfirmation(
          consultantAssignCallListActionForm.getValues("callOrContactLists").length,
          totalRows,
          entitySingular,
          entityPlural
        )}
      </Typography>
      <Typography variant="body1">{locale.youWillBeNotifiedViaEmail}</Typography>
    </Box>
  );

  const onSubmit = consultantAssignCallListActionForm.handleSubmit((data) =>
    assignConsultantCallList({
      callContactListIds: data.callOrContactLists.map((list) => list._id),
      consultantIds: parsedValues.nodeIds
    })
  );

  const reset = () => {
    consultantAssignCallListActionForm.reset();
  };

  const nextStepDisabled = !consultantAssignCallListActionForm.formState.isValid;

  return {
    actionContent,
    confirmationContent,
    onSubmit,
    reset,
    nextStepDisabled
  };
};

const useStyles = makeStyles((theme) => ({
  confirmationContent: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem"
  }
}));
